import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getDirectionsAutocomplete, getGeoPositionByPlaceId } from '../../api';
import { replaceDefaultValues } from '../../helpers/form';
import useCountriesData, {
  getCountryStates,
  getStateCities,
} from '../../hooks/useCountriesData';
import { useGlobalContext } from '../../stores/global';
import FormElement from '../core/FormElement';
import Map from '../Map';
import StepFormSubStepButtons from '../StepForm/StepFormSubStepButtons';
import styles from './Form2.module.scss';

interface Props {
  onSubmit: (data: { [x: string]: any }) => void;
  onBackClick: () => void;
  defaultValues?: any;
}

const DEFAULT_COUNTRY = 'Colombia';

export default function Form2(props: Props) {
  const [mapCenter, setMapCenter] = useState({
    lat: 11.0041072,
    lng: -74.80698129999999,
  });
  const { dispatch, isLoading } = useGlobalContext();
  const {
    data: countriesData,
    isLoading: countriesIsLoading,
  } = useCountriesData();
  const {
    register,
    handleSubmit,
    errors,
    getValues,
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: replaceDefaultValues(
      {
        addressCountry: DEFAULT_COUNTRY,
        addressName: '',
        addressState: '',
        addressCity: '',
        addressNeighbor: '',
      },
      props.defaultValues
    ),
  });

  const handleFormSubmit = (data: any) => {
    const { lat, lng } = mapCenter!;
    data.location = { type: 'Point', coordinates: [lat, lng] };
    props.onSubmit(data);
  };

  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: countriesIsLoading });

    if (!countriesData) return;

    setValue('addressState', props.defaultValues?.addressState);
    setValue('addressCity', props.defaultValues?.addressCity);
    reset(); // need it to actually keep city value set above
  }, [countriesData]);

  const watchAddressState = watch('addressState');
  const watchAddressCity = watch('addressCity');

  useEffect(() => {
    if (!countriesData || isLoading) return;

    const { addressState, addressCity } = getValues();

    if (!addressState || !addressCity) return;

    const [defaultLat, defaultLng] =
      props.defaultValues?.location?.coordinates || [];
    if (
      props.defaultValues?.addressState === watchAddressState &&
      props.defaultValues?.addressCity === watchAddressCity &&
      defaultLat &&
      defaultLng
    ) {
      setMapCenter({ lat: defaultLat, lng: defaultLng });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    getDirectionsAutocomplete(`${addressState} ${addressCity}`)
      .then((response) => response.data.data?.predictions?.[0]?.place_id)
      .then((placeId) => {
        if (placeId) return getGeoPositionByPlaceId(placeId);
        throw 'no placeId';
      })
      .then((response) => response.data.data?.result.geometry.location)
      .then((location) => {
        const [defaultLat, defaultLng] =
          props.defaultValues?.location?.coordinates || [];
        if (
          props.defaultValues?.addressState === watchAddressState &&
          props.defaultValues?.addressCity === watchAddressCity &&
          defaultLat &&
          defaultLng
        ) {
          setMapCenter({ lat: defaultLat, lng: defaultLng });
          return;
        }

        setMapCenter(location);
      })
      .finally(() => dispatch({ type: 'SET_LOADING', payload: false }));
  }, [watchAddressState, watchAddressCity]);

  const states = getCountryStates(DEFAULT_COUNTRY, countriesData);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <h1>¿Dónde esta ubicada tu propiedad?</h1>

      <FormElement
        name="addressState"
        placeholder="Departamento"
        label="Departamento*"
        inputRef={register({ required: true })}
        error={errors.addressState}
        elementType="select"
        options={states}
      />

      <FormElement
        name="addressCity"
        placeholder="Ciudad"
        label="Ciudad*"
        inputRef={register({ required: true })}
        error={errors.addressCity}
        elementType="select"
        options={getStateCities(watchAddressState, states)}
      />

      <FormElement
        name="addressNeighbor"
        label="Barrio*"
        placeholder="Barrio"
        inputRef={register({ required: true })}
        error={errors.addressNeighbor}
        variant="outlined"
        elementType="text"
      />

      <FormElement
        name="addressName"
        label="Dirección como la conoces*"
        placeholder="ejemplo: Calle 55 # 55B-55 Apto 20"
        inputRef={register}
        error={errors.addressName}
        variant="outlined"
        elementType="text"
      />

      <FormElement
        label="Selecciona la dirección exacta en el mapa"
        elementType="custom"
      >
        <div className={styles.mapContainer}>
          <Map
            className={styles.map}
            options={{
              gestureHandling: 'cooperative',
              mapTypeControl: false,
              zoomControl: true,
              panControl: true,
              fullscreenControl: false,
              keyboardShortcuts: false,
              minZoom: 8,
              maxZoom: 18,
            }}
            zoom={13}
            center={mapCenter}
            onDragEnd={(map) => {
              setMapCenter({
                lat: map!.getCenter().lat(),
                lng: map!.getCenter().lng(),
              });
            }}
          />
          <i className="fonticons fonticons-pin" />
        </div>
      </FormElement>

      <StepFormSubStepButtons
        onBackClick={props.onBackClick}
        disabled={countriesIsLoading || isLoading}
      />
    </form>
  );
}
